import { useState, type FormEvent } from 'react';

import { useAuth } from '../../auth/useAuth.ts';
import { describeError } from '../../ui/problemDetails.ts';
import { MemberChip } from './MemberChip.tsx';
import type { Group } from './groupsApi.ts';
import { useRemoveMember, useUpdateGroup } from './useGroups.ts';

/**
 * Rename the group, decide who may invite, and manage who is in it.
 *
 * Renaming and the invite setting are owner-only, matching the API — a member sees the
 * people and their colours, but no controls that would only fail.
 */
export function GroupSettings({ group }: { group: Group }) {
  const { user } = useAuth();
  const update = useUpdateGroup(group.id);
  const removeMember = useRemoveMember(group.id);

  const [name, setName] = useState(group.name);
  const [confirming, setConfirming] = useState<string | null>(null);

  const isOwner = group.yourRole === 'Owner';
  const trimmed = name.trim();

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (trimmed.length > 0) {
      update.mutate({ name: trimmed, allowMemberInvites: group.allowMemberInvites });
    }
  }

  return (
    <section className="settings-section">
      <h3 className="settings-section__title">This group</h3>

      {isOwner && (
        <>
          <form className="settings-row" onSubmit={handleSubmit}>
            <input
              className="settings-row__input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-label="Group name"
              maxLength={80}
              required
            />
            <button
              className="button button--primary button--small"
              type="submit"
              disabled={update.isPending || trimmed === group.name || trimmed.length === 0}
            >
              Save
            </button>
          </form>

          {/* The per-group invite setting: owner-only by default, and this is what lets
              an owner open it up for a flatshare where everyone is trusted. */}
          <label className="toggle">
            <input
              type="checkbox"
              checked={group.allowMemberInvites}
              onChange={(event) =>
                update.mutate({ name: group.name, allowMemberInvites: event.target.checked })
              }
              disabled={update.isPending}
            />
            <span>
              Anyone can invite
              <span className="toggle__hint">
                {group.allowMemberInvites
                  ? 'Any member can send invitations.'
                  : 'Only you can send invitations.'}
              </span>
            </span>
          </label>
        </>
      )}

      <ul className="settings-people">
        {group.members.map((member) => {
          const isYou = member.userId === user?.id;
          // You can always leave; an owner can also remove anyone else. The API refuses
          // to let an owner leave their own group, so that action is not offered.
          const canRemove = isYou ? !isOwner : isOwner;

          return (
            <li key={member.userId} className="settings-person">
              <span>
                <MemberChip member={member} showRole />
                {isYou && <span className="muted"> — you</span>}
              </span>

              {canRemove &&
                (confirming === member.userId ? (
                  <button
                    type="button"
                    className="link-button link-button--danger"
                    onClick={() => removeMember.mutate(member.userId)}
                    disabled={removeMember.isPending}
                  >
                    {isYou ? 'Really leave?' : 'Really remove?'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setConfirming(member.userId)}
                  >
                    {isYou ? 'Leave' : 'Remove'}
                  </button>
                ))}
            </li>
          );
        })}
      </ul>

      {!isOwner && (
        <p className="settings-hint">
          Only{' '}
          {group.members.find((member) => member.role === 'Owner')?.displayName ?? 'the owner'} can
          rename this group.
        </p>
      )}

      {(update.error ?? removeMember.error) && (
        <p className="form-error" role="alert">
          {describeError(update.error ?? removeMember.error)}
        </p>
      )}
    </section>
  );
}
